import { MailSearch } from "lucide-react";
import { useEffect, useState } from "react";
import { FiEye, FiTrash2 } from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";

import { ModalWrapper } from "../../components/AdminSideComponents/common/ModalWrapper";
import PageHeader from "../../components/AdminSideComponents/common/PageHeader";
import Pagination from "../../components/AdminSideComponents/common/Pagination";

import api from "../../configs/api";
import { confirmAction, showError, showSuccess } from "../../utils/toastUtils";
import { capitalize, formatDate } from "../../utils/helpers";

const InquiryCard = ({ inquiry, onView, onStatusUpdate, onDelete }) => {
  const isNew = inquiry.status === "new";

  return (
    <div className="border border-borderLight bg-surfaceColor p-5 flex flex-col gap-3 hover:shadow-md transition">
      {/* TOP */}
      <div className="flex justify-between items-start">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-headingText capitalize">
            {inquiry?.name}
          </p>
          <p className="text-xs text-descriptionText">{inquiry?.email}</p>
          <p className="text-xs text-descriptionText">{inquiry?.phone}</p>
          <p className="text-sm text-headingText">{inquiry?.calculatorName}</p>
          {/* <p className="text-xs text-descriptionText">
            {inquiry.carrier.description}
          </p> */}
        </div>

        {/* ✅ STATUS BADGE */}
        <span
          className={`text-[10px] px-2 py-1 border rounded-full capitalize ${
            isNew
              ? "bg-yellow-100 text-yellow-700 border-yellow-300"
              : "bg-green-100 text-green-700 border-green-300"
          }`}
        >
          {inquiry.status}
        </span>
      </div>

      {/* ACTIONS */}
      <div className="flex items-center justify-between pt-2 border-t border-borderLight">
        <button
          onClick={() => onView(inquiry)}
          className="flex items-center gap-2 text-xs text-primaryAccent"
        >
          <FiEye /> View
        </button>

        <div className="flex items-center gap-2">
          {/* ✅ STATUS TOGGLE BUTTON */}
          <button
            onClick={() => onStatusUpdate(inquiry, isNew ? "contacted" : "new")}
            className={`text-xs px-2 py-1 border rounded ${
              isNew
                ? "text-green-600 border-green-300 hover:bg-green-50"
                : "text-yellow-600 border-yellow-300 hover:bg-yellow-50"
            }`}
          >
            {isNew ? "Mark Contacted" : "Mark New"}
          </button>

          {/* DELETE */}
          <button
            onClick={() => onDelete(inquiry)}
            className="text-red-500 hover:text-red-600"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminCalculatorInquiry = () => {
  const [inquiries, setInquiries] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const limit = 8;

  const fetchInquiries = async () => {
    try {
      setLoading(true);

      const res = await api.get(
        `/calculator-inquiries?page=${page}&limit=${limit}&search=${search}`,
      );

      console.log("response", res);
      setInquiries(res.data.data.inquiries);
      setPagination(res.data.data.pagination);
    } catch (err) {
      console.error(err);
      showError("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, [page, search]);

  const handleStatusUpdate = async (inquiry, status) => {
    if (status === inquiry.status) return;

    const confirmed = await confirmAction({
      title: "Update Status?",
      text: `Change status to "${status}"`,
    });

    if (!confirmed) return;

    try {
      const res = await api.patch(
        `/calculator-inquiries/${inquiry._id}/status`,
        {
          status,
        },
      );

      setInquiries((prev) =>
        prev.map((i) => (i._id === inquiry._id ? { ...i, status } : i)),
      );

      showSuccess("Status updated");
      console.log("staus updated", res.status);
    } catch (err) {
      console.error("Error :", err);
      showError("Failed to update status");
    }
  };

  const handleDelete = async (inquiry) => {
    const confirmed = await confirmAction({
      title: "Delete Inquiry?",
      text: "This action cannot be undone.",
    });

    if (!confirmed) return;

    try {
      await api.delete(`/calculator-inquiries/${inquiry._id}`);

      setInquiries((prev) => prev.filter((i) => i._id !== inquiry._id));

      showSuccess("Inquiry deleted");
    } catch (err) {
      console.error("Error :", err);
      showError("Failed to delete inquiry");
    }
  };

  const total = pagination?.total ?? 0;

  return (
    <div className="p-5">
      <PageHeader
        title="Calculator Inquiries"
        breadcrumb="Dashboard / Calulator-Inquiries"
        customSection={
          <span className="text-xs font-medium text-descriptionText border border-borderLight px-2.5 py-1 bg-surfaceColor">
            {total} inquiries
          </span>
        }
      />

      {/* SEARCH */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-descriptionText">
            <IoIosSearch className="size-4" />
          </span>

          <input
            type="text"
            placeholder="Search inquiries..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-[42px] w-full sm:w-[25rem] pl-9 pr-4 border border-borderLight outline-none focus:border-primaryAccent text-sm"
          />
        </div>
      </div>

      {/* GRID */}
      <div className="overflow-x-auto border border-borderLight bg-surfaceColor">
        {loading ? (
          <div className="p-5 space-y-3">
            {Array.from({ length: limit }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : inquiries.length === 0 ? (
          <EmptyState />
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-borderLight">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Name</th>
                <th className="px-4 py-3 text-left font-semibold">Email</th>
                <th className="px-4 py-3 text-left font-semibold">Phone</th>
                <th className="px-4 py-3 text-left font-semibold">
                  Calculator
                </th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-center font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {inquiries.map((inq) => {
                const isNew = inq.status === "new";

                return (
                  <tr
                    key={inq._id}
                    className="border-b border-borderLight hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-medium">{inq.name}</td>

                    <td className="px-4 py-3">{inq.email}</td>

                    <td className="px-4 py-3">{inq.phone}</td>

                    <td className="px-4 py-3">{inq.calculatorName}</td>

                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() =>
                          handleStatusUpdate(inq, isNew ? "contacted" : "new")
                        }
                        className={`text-xs px-3 py-1 rounded-full border capitalize transition ${
                          isNew
                            ? "bg-yellow-100 text-yellow-700 border-yellow-300 hover:bg-yellow-200"
                            : "bg-green-100 text-green-700 border-green-300 hover:bg-green-200"
                        }`}
                      >
                        {inq.status}
                      </button>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setSelectedInquiry(inq)}
                          className="text-primaryAccent hover:text-blue-700"
                        >
                          <FiEye size={18} />
                        </button>

                        <button
                          onClick={() => handleDelete(inq)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FiTrash2 size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && (
        <Pagination
          pagination={pagination}
          page={page}
          onPageChange={setPage}
        />
      )}

      {/* VIEW MODAL */}
      <ModalWrapper
        title="Inquiry Details"
        isOpen={!!selectedInquiry}
        onClose={() => setSelectedInquiry(null)}
        showFooter={false}
        maxWidth="max-w-xl"
      >
        {selectedInquiry && (
          <div className="space-y-6 text-sm">
            {/* Header */}
            <div className="border-b border-borderLight pb-4">
              <p className="text-lg font-semibold text-headingText">
                {selectedInquiry.name}
              </p>
              <p className="text-xs text-descriptionText">
                {selectedInquiry.email}
              </p>
              <p className="text-xs text-descriptionText">
                {selectedInquiry.phone}
              </p>
            </div>

            <div className="bg-surfaceColor border border-borderLight p-3 col-span-2">
              <p className="text-xs text-descriptionText mb-1">
                Calculator Name
              </p>
              <p className="font-medium text-headingText">
                {selectedInquiry?.calculatorName || "N/A"}
              </p>
            </div>

            {/* Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-surfaceColor border border-borderLight p-3">
                <p className="text-xs text-descriptionText mb-1">Status</p>
                <p className="font-medium text-headingText capitalize">
                  {selectedInquiry.status}
                </p>
              </div>
            </div>
          </div>
        )}
      </ModalWrapper>
    </div>
  );
};

const EmptyState = () => (
  <div className="col-span-full flex items-center justify-center py-24">
    <div className="max-w-sm border border-dashed border-borderLight p-10 text-center">
      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primaryAccent/10 text-primaryAccent mb-4 mx-auto">
        <MailSearch size={28} />
      </div>

      <p className="text-lg font-semibold text-headingText">
        No inquiries found
      </p>
    </div>
  </div>
);

const SkeletonCard = () => (
  <div className="border border-borderLight bg-surfaceColor p-5 animate-pulse space-y-3">
    <div className="h-3 bg-divider w-1/3" />
    <div className="h-3 bg-divider w-2/3" />
    <div className="h-3 bg-divider w-full" />
    <div className="h-3 bg-divider w-1/2" />
  </div>
);

export default AdminCalculatorInquiry;
